import Markdown from 'markdown-to-jsx';
import type { ReactNode } from 'react';
import { Text } from '@bridebook/aura';

// markdown-to-jsx emits bare <h3>/<ul>/<li>/<p> tags, style them with tw.
const markdownClasses = [
  'leading-[150%]',
  '[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:text-sm [&_h4]:font-semibold',
  '[&_p]:mb-3',
  '[&_a]:text-indigo-crush [&_a]:underline',
  '[&_ul]:mb-3 [&_ul]:ml-6 [&_ul]:list-disc',
  '[&_ol]:mb-3 [&_ol]:ml-6 [&_ol]:list-decimal',
  '[&_li]:mb-1 [&_li]:list-item',
].join(' ');

// The page owns the single <h1> and the section <h2>, so drop any <h1>/<h2>.
const Unwrap = ({ children }: { children?: ReactNode }) => <>{children}</>;

const markdownOptions = {
  disableParsingRawHTML: true,
  overrides: {
    h1: Unwrap,
    h2: Unwrap,
  },
} as const;

interface IProps {
  embedUrl: string;
  // iframe accessible title and h1-level fallback
  title: string;
  videoTitle: string;
  videoDescription?: string;
}

/**
 * Shared presentation for the venue video: a responsive 16:9 YouTube embed with the
 * video title and an optional markdown description, on the tinted card background.
 * Used by both the crawlable basic page and the logged-in page so styling stays in sync.
 */
export const SupplierVideoEmbed = ({ embedUrl, title, videoTitle, videoDescription }: IProps) => (
  <div className="mt-4 mb-6 rounded-2xl bg-indigo-crush-10 p-4 sm:p-6">
    <div className="relative h-0 w-full overflow-hidden rounded-xl pb-[56.25%]">
      <iframe
        className="absolute inset-0 h-full w-full border-0"
        src={`${embedUrl}?rel=0`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>

    <Text as="h2" variant="typo-xl-semibold" className="mt-4 mb-1">
      {videoTitle}
    </Text>
    {videoDescription && (
      <div className={markdownClasses}>
        <Markdown options={markdownOptions}>{videoDescription}</Markdown>
      </div>
    )}
  </div>
);
